import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { Form } from "react-bootstrap";
import DataTable from "@components/DataTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faCheck,
  faX,
  faPencil,
  faTrashCan,
} from "@fortawesome/pro-solid-svg-icons";
import {
  useCreateProductAccountingMutation,
  useUpdateProductAccountingMutation,
  useDeleteProductAccountingMutation,
  useListProductAccountingsForAccountQuery,
  V1ProductAccountingResource,
} from "@generated";
import useListServiceCategories from "../api/useListServiceCategories";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ConfirmationModal from "@components/Modals/ConfirmationModal";

interface Props {
  productId: number;
  editing?: boolean;
  onSave?: () => void;
}

const AccountingCodesTable: React.FC<Props> = ({
  productId,
  editing = true,
  onSave,
}) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const [acctCodes, setAcctCodes] = useState<any[]>([]);
  const [initialAcctCodes, setInitialAcctCodes] = useState<any[]>([]);
  const [focusedId, setFocusedId] = useState<string | null>(null);

  const [createAcctCode] = useCreateProductAccountingMutation();
  const [updateAcctCode] = useUpdateProductAccountingMutation();
  const [deleteAcctCode] = useDeleteProductAccountingMutation();

  const [successMessage, setSuccessMessage] = useState<string>("");
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showFailAlert, setShowFailAlert] = useState<boolean>(false);

  const [itemToDelete, setItemToDelete] = useState<{ id: string } | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] =
    useState<boolean>(false);

  const unsavedItem: boolean = [...acctCodes].some(
    (i) => i.editing && i.isChanged
  );

  const getStateItems = (type) => {
    if (type === "product-accountings") {
      return {
        state: acctCodes,
        setState: setAcctCodes,
        initial: initialAcctCodes,
        setInitial: setInitialAcctCodes,
      };
    }
    throw new Error(`Unknown type: ${type}`);
  };

  const { data: queryData, refetch } = useListProductAccountingsForAccountQuery(
    {
      slug: accountSlug,
      productId,
      include: ["service-category"],
    }
  );

  const { serviceCategories } = useListServiceCategories();

  const fetchAcctCodes = () => {
    const records = queryData?.data || [];
    const included = queryData?.included || [];

    const result = records.map((item) => {
      const id = item.id;
      const code = item.attributes?.code || "";

      const categoryId = item.relationships?.["service-category"]?.data?.id;
      const category = included.find(
        (inc) => inc.id === categoryId && inc.type === "service-categories"
      );
      const nestedName = category?.attributes?.["nested-name"] || "";

      return {
        id,
        type: "product-accountings",
        code,
        serviceCategoryId: categoryId ? Number(categoryId) : undefined,
        category: category
          ? [
              {
                id: Number(category.id),
                type: "service-categories",
                attributes: { nestedName },
              },
            ]
          : [],
        editing: false,
        isChanged: false,
      };
    });
    setAcctCodes(result);
    setInitialAcctCodes(result);
  };

  useEffect(() => {
    if (queryData?.data) fetchAcctCodes();
  }, [queryData]);

  const updateItem = (item) => {
    const { state, setState } = getStateItems(item.type);
    setState(
      state.map((i) => {
        return i.id === item.id ? item : i;
      })
    );
  };

  const confirmItem = async (item) => {
    const data: V1ProductAccountingResource = {
      type: "product-accountings",
      attributes: { code: item.code },
      relationships: {
        product: { data: { id: productId, type: "products" } },
        ...(item.serviceCategoryId && {
          "service-category": {
            data: { id: item.serviceCategoryId, type: "service-categories" },
          },
        }),
      },
    };

    const arg = {
      slug: accountSlug,
      productId,
      body: { data },
    };

    try {
      const isNew = item.id.includes("new");
      const res = isNew
        ? // @ts-ignore
          await createAcctCode(arg).unwrap()
        : // @ts-ignore
          await updateAcctCode({
            ...arg,
            id: Number(item.id),
            body: { data: { ...data, id: Number(item.id) } },
          }).unwrap();

      const id = res?.data?.id;

      if (id) {
        setSuccessMessage(`Accounting Code ${isNew ? "created" : "updated"}`);
        setShowSuccessAlert(true);
        refetch();
        onSave?.();
      }
    } catch (err) {
      console.error("Failed to save accounting code", err);

      let detail = "unknown error";
      if (typeof err === "object" && err !== null) {
        const errorData = (err as any).data ?? (err as any).error?.data;
        if (errorData?.errors?.[0]?.detail) {
          detail = errorData.errors[0].detail;
        }
      }

      setErrorMessage(detail);
      setShowFailAlert(true);
    }
  };

  const deleteItem = async () => {
    if (!itemToDelete) return;

    try {
      await deleteAcctCode({
        slug: accountSlug,
        productId,
        id: Number(itemToDelete.id),
      }).unwrap();

      setSuccessMessage("Accounting Code deleted successfully!");
      setShowSuccessAlert(true);
      setShowConfirmDelete(false);
      refetch();
    } catch (err) {
      console.error("Failed to delete accounting code", err);
      setErrorMessage("Failed to delete accounting code");
      setShowFailAlert(true);
    }
  };

  const addNewCode = () => {
    if (unsavedItem) {
      setShowUnsavedChangesModal(true);
      return;
    }
    const id = `new-${Date.now()}`;
    const firstCategory = serviceCategories?.[0];
    const serviceCategoryId = firstCategory?.id;
    const nestedName = firstCategory?.attributes?.["nested-name"] || "";

    setAcctCodes([
      ...acctCodes.map((i) => ({ ...i, editing: false })),
      {
        id,
        type: "product-accountings",
        code: "",
        editing: true,
        isChanged: true,
        category: serviceCategoryId
          ? [
              {
                id: serviceCategoryId,
                type: "service-categories",
                attributes: { nestedName },
              },
            ]
          : [],
        serviceCategoryId: serviceCategoryId
          ? Number(serviceCategoryId)
          : undefined,
      },
    ]);
  };

  const cancelItem = (item) => {
    const { state, setState, initial } = getStateItems(item.type);

    const original = initial.find((i) => i.id === item.id);

    if (!original) {
      setState(state.filter((i) => i.id !== item.id));
    } else {
      setState(
        state.map((i) =>
          i.id === item.id
            ? { ...original, editing: false, isChanged: false }
            : i
        )
      );
    }
  };

  const columns = [
    {
      id: "code",
      header: "Accounting Code",
      size: 50,
      cell(props) {
        const item = props.row.original;
        return item.editing ? (
          <Form.Control
            value={item.code}
            onChange={(e) =>
              updateItem({ ...item, code: e.target.value, isChanged: true })
            }
            style={{ width: "100%" }}
            autoFocus={focusedId === item.id + "code"}
            onFocus={() => setFocusedId(item.id + "code")}
          />
        ) : (
          <div className="edit">
            <FontAwesomeIcon
              icon={faPencil}
              onClick={() => {
                setFocusedId(item.id + "code");
                setAcctCodes((prev) =>
                  prev
                    .filter((i) => !i.id.startsWith("new"))
                    .map((i) =>
                      i.id === item.id
                        ? { ...i, editing: true }
                        : { ...i, editing: false }
                    )
                );
              }}
            />
            <span>{item.code}</span>
          </div>
        );
      },
    },
    {
      id: "category",
      header: "Category/LOB",
      size: 50,
      cell(props) {
        const item = props.row.original;
        return item.editing ? (
          <div className="editCategoryRow">
            <div className="categoryDropdownWrapper">
              <Form.Select
                className="categoryDropdown"
                value={item.serviceCategoryId ?? ""}
                style={{ width: "100%" }}
                onChange={(e) =>
                  updateItem({
                    ...item,
                    serviceCategoryId: Number(e.target.value),
                    category: serviceCategories
                      .filter((cat) => cat.id === Number(e.target.value))
                      .map((cat) => ({
                        id: Number(cat.id),
                        type: "service-categories",
                        attributes: {
                          nestedName: cat.attributes?.["nested-name"] || "",
                        },
                      })),
                    isChanged: true,
                  })
                }
              >
                <option value="">Select a Category</option>
                {serviceCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.attributes?.["nested-name"] || "(No Name)"}
                  </option>
                ))}
              </Form.Select>
              <div className="dropdownArrow">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path d="M7 10L12 15L17 10H7Z" fill="#212957" />
                </svg>
              </div>
            </div>
            <div className="actionsCell">
              <FontAwesomeIcon
                icon={faCheck}
                onClick={() => confirmItem(item)}
                className="actionIcon"
              />
              <FontAwesomeIcon
                icon={faX}
                onClick={() => cancelItem(item)}
                className="actionIcon"
              />
            </div>
          </div>
        ) : (
          <div className="nonEditingCell">
            <span>
              {item.category
                ?.map((cat) => cat.attributes?.nestedName)
                .join(", ") || "-"}
            </span>
            <FontAwesomeIcon
              icon={faTrashCan}
              onClick={() => {
                setItemToDelete({ id: item.id });
                setShowConfirmDelete(true);
              }}
              className="deleteIcon"
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}

      <section>
        <p className="tableTitle">Accounting Codes</p>
        <div className="acctCodesTable">
          {acctCodes.length > 0 && (
            <DataTable
              data={acctCodes}
              columns={columns}
              striped
              bordered
              hover
            />
          )}
        </div>
        {editing && (
          <div className="add" onClick={addNewCode}>
            <FontAwesomeIcon icon={faPlus} />
            <span className="addText">Add Accounting Code</span>
          </div>
        )}
      </section>

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showConfirmDelete}
        title="Delete Accounting Code"
        message="Are you sure you want to delete this accounting code?"
        onConfirm={deleteItem}
        onCancel={() => setShowConfirmDelete(false)}
      />
    </>
  );
};

export default AccountingCodesTable;
